import { mutatePurchaseOrders } from "@/api/purchaseOrder/usePurchaseOrder";
import { users } from "@/api/users/useUsersQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import { PurchaseOrderItemsCreateForm } from "@/features/purchaseOrderItems/form/CreateForm";
import { FormButtonBox } from "@/features/standard/form/FormButtonBox";
import { FormMain } from "@/features/standard/form/FormMain";
import { FormSelect } from "@/features/standard/form/FormSelect";
import { FormWrapper } from "@/features/standard/form/FormWrapper";
import customAlert from "@/features/ui/alert/Alert";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import { PartnerAutoComplete } from "@/features/ui/autoComplete/partner/partner-autoComplete";
import { PurchaseOrderCategoryAutoComplete } from "@/features/ui/autoComplete/purchaseOrderCategory/purchaseOrderCategory-autoComplete";
import { UserAutoComplete } from "@/features/ui/autoComplete/user/user-autoComplete";
import { ConfirmForm } from "@/features/ui/confirm";
import { handleErrorResponse } from "@/utils/errorMessage";
import { translateWithPartnerType } from "@/utils/partnerType";
import { validateFormValues } from "@/utils/validation";
import { Button, Flex, Text, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import {
  PartnersGet200ResponseRowsInnerTypeEnum,
  ScmApiPurchaseOrdersPostRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconCalendar } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const Create = Object.assign(FormMain, {
  Button: Button,
  Input: TextInput,
  Select: FormSelect,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
  Text: Text,
  Flex: Flex,
  DateInput: DateInput,
});

interface PurchaseOrderCreateFormProps {
  code?: string;
  autoCompleteOnSubmit?: (values: any) => void;
}

export const PurchaseOrderCreateForm = (params: PurchaseOrderCreateFormProps) => {
  const { code, autoCompleteOnSubmit } = params;
  const { openModal, closeModal } = useModal();

  const { data: userInfo } = useQuery(users.who({}));
  const partnerType = userInfo?.data?.partner?.type;
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate: createMutate } = useMutation(
    async (params: ScmApiPurchaseOrdersPostRequest) => {
      // mutationFn을 비동기 함수로 정의
      return await mutatePurchaseOrders
        .create(params)
        .mutationFn(params as ScmApiPurchaseOrdersPostRequest | any);
    },
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(["purchaseOrders"]); // 성공 시 'purchaseOrders' 캐시 무효화
        closeModal(form.values);
        customAlert(
          t("message.createSuccess", {
            item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
          }),
          t("common.createSuccess"),
          "green",
        );

        openModal(
          <ConfirmForm
            message={t("purchaseOrderItems.createMessage")}
            yesCallback={() => {
              openModal(
                <PurchaseOrderItemsCreateForm purchaseOrder={res.data} />,
                null,
                t("common.additional", {
                  item: translateWithPartnerType(
                    t,
                    "purchaseOrderItems.purchaseOrderItems",
                    partnerType,
                  ),
                }),
                true,
              ).then(() => closeModal(null));
            }}
            noCallback={() => closeModal(null)}
          />,
          null,
          t("common.additional", {
            item: translateWithPartnerType(t, "purchaseOrderItems.purchaseOrderItems", partnerType),
          }),
          true,
        );
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({
            error,
            errorContext: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
          }),
          t("common.createFail", {
            item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
          }),
          "red",
        );
      },
    },
  );
  const form = useForm({
    initialValues: {
      partnerCode: undefined,
      // status : undefined,
      code: code ?? undefined,
      name: undefined,
      purchaseOrderCategoryName: undefined,
      orderDate: new Date(),
      deliveryDeadline: undefined,
      userCode: undefined,
      sourceLocationCode: undefined,
    },
  });

  const onSubmit = async () => {
    try {
      createMutate({
        purchaseOrdersGetRequest: {
          partnerCode: form.values.partnerCode!,
          direction: "INBOUND",
          code: code ?? form.values.code!,
          name: form.values.name!,
          purchaseOrderCategoryName: form.values.purchaseOrderCategoryName,
          // status : form.values.status!,
          orderDate: form.values.orderDate!.toISOString().split("T")[0],
          deliveryDeadline: form.values.deliveryDeadline!,
          userCode: form.values.userCode!,
          sourceLocationCode: form.values.sourceLocationCode!,
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({
          error,
          errorContext: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
        }),
        t("common.createFail", {
          item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
        }),
        "red",
      );
    }
  };

  return (
    <>
      <Create.Wrapper>
        <PartnerAutoComplete
          label={t("partner.partner")}
          placeholder={t("placeholder.select", { item: t("partner.partner") })}
          {...form.getInputProps("partnerCode")}
          partnerType={[
            partnerType ?? PartnersGet200ResponseRowsInnerTypeEnum.SALES,
            PartnersGet200ResponseRowsInnerTypeEnum.BI_DIRECTION,
          ]}
          withAsterisk
        />
        <Create.Input
          withAsterisk
          label={translateWithPartnerType(t, "purchaseOrder.code", partnerType)}
          placeholder={t("placeholder.input", {
            item: translateWithPartnerType(t, "purchaseOrder.code", partnerType),
          })}
          {...form.getInputProps("code")}
        />
        <Create.Input
          withAsterisk
          label={translateWithPartnerType(t, "purchaseOrder.name", partnerType)}
          placeholder={t("placeholder.input", {
            item: translateWithPartnerType(t, "purchaseOrder.name", partnerType),
          })}
          {...form.getInputProps("name")}
        />
        <PurchaseOrderCategoryAutoComplete
          label={translateWithPartnerType(t, "purchaseOrder.categoryName", partnerType)}
          placeholder={t("placeholder.select", {
            item: translateWithPartnerType(t, "purchaseOrder.categoryName", partnerType),
          })}
          {...form.getInputProps("purchaseOrderCategoryName")}
        />
        <UserAutoComplete
          label={t("order.manager")}
          placeholder={t("placeholder.select", { item: t("order.manager") })}
          {...form.getInputProps("userCode")}
        />
        <LocationAutoComplete
          label={translateWithPartnerType(t, "purchaseOrder.toLocationName", partnerType)}
          placeholder={t("placeholder.select", {
            item: translateWithPartnerType(t, "purchaseOrder.toLocationName", partnerType),
          })}
          {...form.getInputProps("sourceLocationCode")}
        />
        <Create.DateInput
          popoverProps={{ withinPortal: true }}
          icon={<IconCalendar size="1.2rem" />}
          label={translateWithPartnerType(t, "purchaseOrder.orderDate", partnerType)}
          valueFormat="YYYY-MM-DD"
          defaultValue={new Date()} // 오늘 날짜를 기본값으로 설정
          {...form.getInputProps("orderDate")}
        />
        <Create.DateInput
          popoverProps={{ withinPortal: true }}
          icon={<IconCalendar size="1.2rem" />}
          label={t("order.deliveryDate")}
          valueFormat="YYYY-MM-DD"
          {...form.getInputProps("deliveryDeadline")}
        />
        <Create.ButtonBox>
          <Create.Button color="gray" onClick={closeModal}>
            {t("common.cancel")}
          </Create.Button>
          <Create.Button
            disabled={validateFormValues({
              pageType: "purchaseOrder",
              values: form.values,
            })}
            onClick={autoCompleteOnSubmit ? () => autoCompleteOnSubmit(form.values) : onSubmit}
          >
            {t("common.create", {
              item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
            })}
          </Create.Button>
        </Create.ButtonBox>
      </Create.Wrapper>
    </>
  );
};
